import { useTranslations } from '@vocab/react';
import { Box } from 'braid-design-system';

import {
  COLUMN_WIDTH_ACTIONS,
  COLUMN_WIDTH_CANDIDATE_MATCHES,
  COLUMN_WIDTH_CANDIDATES,
  COLUMN_WIDTH_CTA,
  COLUMN_WIDTH_PERFORMANCE,
  COLUMN_WIDTH_STATUS,
} from 'src/constants/table';
import type { JobListItemFragment } from 'src/graphql/__generated__';

import type { QueryParamTab } from '../../../hooks/useQueryParam';
import { JobActions } from '../JobActions/JobActions';
import { JobNumOfCandidates } from '../JobNumOfCandidates/JobNumOfCandidates';
import { JobPerformance } from '../JobPerformance/JobPerformance';
import { JobStatus } from '../JobStatus/JobStatus';
import { JobTalentSearch } from '../JobTalentSearch/JobTalentSearch';
import { JobTitleAndLocation } from '../JobTitleAndLocation/JobTitleAndLocation';
import { JobUpgrades } from '../JobUpgrades';
import { Table, TableCell, TableHeaderCell } from '../Table';

import translations from './.vocab';

interface Props {
  jobs: JobListItemFragment[];
  loading?: boolean;
  tab: QueryParamTab;
}

export const JobListDesktopAndTablet = ({
  jobs,
  loading = false,
  tab,
}: Props) => {
  const { t } = useTranslations(translations);

  return (
    <Table>
      <Box component="thead">
        <tr>
          <TableHeaderCell paddingLeft="medium">{t('Status')}</TableHeaderCell>
          <TableHeaderCell>{t('Job')}</TableHeaderCell>
          <TableHeaderCell>{t('Candidates')}</TableHeaderCell>
          {tab === 'open' && (
            <TableHeaderCell>{t('Talent Search')}</TableHeaderCell>
          )}
          <TableHeaderCell>{t('Performance')}</TableHeaderCell>
          {tab === 'open' && (
            <TableHeaderCell dataTestId="cta-header">&nbsp;</TableHeaderCell>
          )}
          <TableHeaderCell paddingRight="medium" align="right">
            {t('Job Actions')}
          </TableHeaderCell>
        </tr>
      </Box>

      <Box component="tbody">
        {jobs.map((job) => {
          const {
            id,
            candidatesCount,
            newCandidatesCount,
            daysRemaining,
            status,
            locations,
            owner,
            postType,
            advertiserJobReference,
            createdDate,
            title,
          } = job;
          const location = locations?.[0];
          const createdBy = owner?.fullName;
          return (
            <tr key={id} data-testid={`job-row-${id}`}>
              <TableCell
                width={COLUMN_WIDTH_STATUS}
                minWidth={COLUMN_WIDTH_STATUS}
                maxWidth={COLUMN_WIDTH_STATUS}
                paddingLeft="medium"
              >
                <JobStatus
                  jobId={id}
                  isDraft={false}
                  daysRemaining={daysRemaining ?? 0}
                  status={status}
                  loading={loading}
                />
              </TableCell>

              <TableCell minWidth={80} maxWidth={250}>
                <JobTitleAndLocation
                  id={id}
                  isDraft={false}
                  postType={postType}
                  advertiserJobReference={advertiserJobReference}
                  createdDate={createdDate}
                  createdBy={createdBy}
                  title={title}
                  location={location}
                  loading={loading}
                />
              </TableCell>

              <TableCell
                width={COLUMN_WIDTH_CANDIDATES}
                minWidth={COLUMN_WIDTH_CANDIDATES}
                maxWidth={COLUMN_WIDTH_CANDIDATES}
              >
                <JobNumOfCandidates
                  jobId={id}
                  numberOfCandidates={candidatesCount ?? 0}
                  numberOfNewCandidates={newCandidatesCount ?? 0}
                  createdDate={createdDate}
                  status={status}
                  loading={loading}
                />
              </TableCell>

              {tab === 'open' && (
                <TableCell
                  width={COLUMN_WIDTH_CANDIDATE_MATCHES}
                  minWidth={COLUMN_WIDTH_CANDIDATE_MATCHES}
                  maxWidth={COLUMN_WIDTH_CANDIDATE_MATCHES}
                >
                  <JobTalentSearch job={job} loading={loading} />
                </TableCell>
              )}

              <TableCell
                width={COLUMN_WIDTH_PERFORMANCE}
                minWidth={COLUMN_WIDTH_PERFORMANCE}
                maxWidth={COLUMN_WIDTH_PERFORMANCE}
              >
                <JobPerformance job={job} loading={loading} />
              </TableCell>

              {tab === 'open' && (
                <TableCell
                  width={COLUMN_WIDTH_CTA}
                  minWidth={COLUMN_WIDTH_CTA}
                  maxWidth={COLUMN_WIDTH_CTA}
                >
                  <JobUpgrades job={job} />
                </TableCell>
              )}

              <TableCell
                width={COLUMN_WIDTH_ACTIONS}
                minWidth={COLUMN_WIDTH_ACTIONS}
                maxWidth={COLUMN_WIDTH_ACTIONS}
                paddingRight="medium"
              >
                <JobActions job={job} loading={loading} />
              </TableCell>
            </tr>
          );
        })}
      </Box>
    </Table>
  );
};
